/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';

import Pagination from 'components/pagination/Pagination';
import BrandsFilter from 'components/brands-filter/BrandsFilter';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Input from 'components/inputs/input/Input';
import Tooltip from 'components/Tooltip';
import ItemsPerPage from 'components/items-per-page/ItemsPerPage';

import ListDashboardResults from 'modules/lists/components/ListDashboardResults';

import { getActiveBrandFilterSelector } from 'selectors/brand-filters/brandFilters';

import { trackEventListsActions } from 'modules/tracking/utils/tracking';
import { fetchDashboardContentLists, resetLists } from 'modules/lists/reducers/lists';

import { __t, applyTranslations } from 'utils/language';
import { updateQueryParams } from 'utils/location';
import { getBrandProfileId } from 'utils/organization';
import { setItem } from 'utils/storage/sessionStorage';
import {
  LIST_DASHBOARD_QUERY_PARAMS,
  LIST_STATE_STORAGE_NAME,
  SMART_LIST_TYPE,
  getQueryValueFromUrl,
  getSortOptions
} from 'utils/list';

import type {
  Data,
  ListScreenState,
  LocationWindow,
  MixedSelectDropdownItem,
  Organization,
  TextEntry
} from 'types';

import './content-list-dashboard.scss';

applyTranslations(require('./content-list-dashboard.i18n.json'));

// since first iteration is only about smart lists, we can have noop for half of the functions
const noop = () => {};

const { SEARCH, SORT, PAGE, FILTER, PER_PAGE } = LIST_DASHBOARD_QUERY_PARAMS;

type OwnProps = {
  location: LocationWindow
};

type StateProps = {
  activeBrandFilter: string,
  currentOrganization: Organization,
  isLoading: boolean,
  isSearchingOrSorting: boolean,
  screenState: ListScreenState
};

type DispatchProps = {
  dispatchFetchDashboardLists: (
    orgId: number,
    type: string,
    filter: string,
    page: number,
    sort: string,
    search: string,
    perPage: number,
    isSearchingOrSorting: boolean
  ) => Promise<void>,
  dispatchResetLists: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

// can't use computed props for type
type ContentListsDashboardScreenState = {
  page: number,
  search: string,
  sort: string,
  filter: string,
  perPage: number
};

type State = {
  searchTerm: string,
  perPage: string
};

const MSG_KEY = 'content_list_dashboard';

class ContentListsDashboard extends React.PureComponent<Props, State> {
  state = {
    searchTerm: getQueryValueFromUrl(this.props.location, SEARCH),
    perPage: getQueryValueFromUrl(this.props.location, PER_PAGE)
  };

  debouncedUpdateSearchQuery = debounce((searchTerm: string, perPage: string) => {
    const { location } = this.props;

    updateQueryParams(
      location,
      {
        [SEARCH]: searchTerm,
        [PER_PAGE]: perPage
      }
    );
  }, 400);

  componentDidMount() {
    const { location } = this.props;
    const brandProfileId = this.getBrandProfileId();
    const sort = getQueryValueFromUrl(location, SORT);
    const search = getQueryValueFromUrl(location, SEARCH);
    const page = Number(getQueryValueFromUrl(location, PAGE));
    const perPage = Number(getQueryValueFromUrl(location, PER_PAGE));

    this.loadData(page, sort, search, perPage);

    if (brandProfileId) {
      trackEventListsActions(SMART_LIST_TYPE);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    const { location } = this.props;
    let sort = getQueryValueFromUrl(location, SORT);
    let search = getQueryValueFromUrl(location, SEARCH);
    let page = getQueryValueFromUrl(location, PAGE);
    const filter = getQueryValueFromUrl(location, FILTER);
    const perPage = getQueryValueFromUrl(location, PER_PAGE);

    const hasBrandFilterChanged = filter !== getQueryValueFromUrl(prevProps.location, FILTER);
    const hasSortChanged = sort !== getQueryValueFromUrl(prevProps.location, SORT);
    const hasSearchChanged = search !== getQueryValueFromUrl(prevProps.location, SEARCH);
    const hasPageChanged = page !== getQueryValueFromUrl(prevProps.location, PAGE);
    const hasPerPageChanged = perPage !== getQueryValueFromUrl(prevProps.location, PER_PAGE);

    if (hasBrandFilterChanged) {
      sort = getSortOptions(SMART_LIST_TYPE, filter)[0].value;
      search = '';

      this.setState({
        searchTerm: search
      });
    }

    // Reset page number to page 1 if any of brand filter or sort has changed
    const shouldResetToFirstPage =
      hasBrandFilterChanged || hasSortChanged || hasSearchChanged || hasPerPageChanged;

    if (shouldResetToFirstPage) {
      page = '1';
    }

    if (shouldResetToFirstPage || hasPageChanged) {
      this.setQueryParams({
        [SORT]: sort,
        [PAGE]: Number(page),
        [SEARCH]: search
      });

      this.loadData(
        Number(page), sort, search, Number(perPage), hasSortChanged || hasSearchChanged || hasPageChanged
      );
    }
  }

  componentWillUnmount() {
    const { dispatchResetLists } = this.props;

    dispatchResetLists();
  }

  getDropdownItems = (sort: string): Array<MixedSelectDropdownItem> => {
    const { activeBrandFilter } = this.props;
    const sortOptions = getSortOptions(SMART_LIST_TYPE, activeBrandFilter);

    return sortOptions.map(item => ({
      identifier: item.value,
      isDisabled: false,
      isSelected: sort === item.value,
      label: item.label,
      value: item.value
    }));
  };

  getBrandProfileId(): number | undefined | null {
    const { currentOrganization } = this.props;

    return getBrandProfileId(currentOrganization);
  }

  setQueryParams(query: Partial<ContentListsDashboardScreenState>): void {
    const { location } = this.props;

    updateQueryParams(location, query);
  }

  loadData(
    page: number,
    sort: string,
    search: string,
    perPage: number,
    isSearchingOrSorting: boolean = false
  ): void {
    const { currentOrganization, location, dispatchFetchDashboardLists, activeBrandFilter } = this.props;
    const orgId = currentOrganization.id;
    const filter = getQueryValueFromUrl(location, FILTER) || activeBrandFilter;

    setItem(LIST_STATE_STORAGE_NAME, location.path || '');

    if (orgId) {
      dispatchFetchDashboardLists(
        orgId, SMART_LIST_TYPE, filter, Number(page), sort, search, perPage, isSearchingOrSorting
      );
    }
  }

  handlePageChange = (page: number): void => {
    const { location } = this.props;

    updateQueryParams(location, { [PAGE]: page });
  };

  handleSortChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { location } = this.props;

    updateQueryParams(location, {
      [SORT]: selectedItem?.identifier || updatedItems[0].identifier
    });
  };

  handleSearchChange = (event: React.SyntheticEvent<TextEntry>): void => {
    const target = event.currentTarget;
    const searchTerm = target.value || '';
    const { perPage } = this.state;

    this.setState({
      searchTerm
    }, () => this.debouncedUpdateSearchQuery(searchTerm, perPage));
  };

  handlePerPageChange = (perPageValue: number): void => {
    const { searchTerm } = this.state;
    const perPage = String(perPageValue);

    this.setState(
      { perPage },
      () => this.debouncedUpdateSearchQuery(searchTerm, perPage)
    );
  };

  renderHeader(): React.ReactNode {
    return (
      <div className="content-list-dashboard__header">
        <div>
          {__t(`${MSG_KEY}.labels.smart_collections`)}
        </div>
        <div className="content-list-dashboard__header-tooltip">
          <Tooltip
            message={__t(`${MSG_KEY}.tooltips.smart`)}
            width="extra-long"
            showQuestionMark
          />
        </div>
      </div>
    );
  }

  renderBrandsDropdown(): React.ReactNode {
    return (
      <div className="content-list-dashboard__filters-section">
        <div className="content-list-dashboard__filters-label">
          {__t(`${MSG_KEY}.labels.filter_by`)}:
        </div>
        <BrandsFilter hideLabel />
      </div>
    );
  }

  renderSearch(): React.ReactNode {
    const { searchTerm } = this.state;

    return (
      <div className="content-list-dashboard__filters-section content-list-dashboard__filters-section--search">
        <Input
          placeholder={__t(`${MSG_KEY}.placeholders.search`)}
          value={searchTerm}
          width="responsive"
          className="content-list-dashboard__input"
          onChange={this.handleSearchChange}
        />
      </div>
    );
  }

  renderSort(): React.ReactNode {
    const { searchTerm } = this.state;
    const { location } = this.props;
    const sort = getQueryValueFromUrl(location, SORT);
    const dropdownItems = this.getDropdownItems(sort);

    return (
      <div className="content-list-dashboard__filters-section">
        <Tooltip
          message={
            searchTerm.length > 0
              ? __t(`${MSG_KEY}.tooltips.disabled_sort`)
              : undefined
          }
        >
          <div className="content-list-dashboard__filters-sort">
            <div className="content-list-dashboard__filters-label">
              {__t(`${MSG_KEY}.labels.sort_by`)}:
            </div>
            <MixedSelectDropdown
              key={`content-list-dashboard__dropdown--${sort}`}
              name="content-list-dashboard__dropdown"
              items={dropdownItems}
              isDisabled={searchTerm.length > 0}
              withSelectedInList
              className="content-list-dashboard__dropdown"
              onItemsToggle={this.handleSortChange}
            />
          </div>
        </Tooltip>
      </div>
    );
  }

  renderItemsPerPage(): JSX.Element {
    const { perPage } = this.state;

    return (
      <div className="content-list-dashboard__items-per-page-section">
        <ItemsPerPage
          perPage={Number(perPage)}
          onChange={this.handlePerPageChange}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { screenState, location, isLoading, isSearchingOrSorting } = this.props;
    const pagination = screenState.pagination;

    return (
      <div className="content-list-dashboard">
        {this.renderHeader()}
        <div className="content-list-dashboard__filters">
          {this.renderBrandsDropdown()}
          {this.renderSearch()}
          {this.renderItemsPerPage()}
          {this.renderSort()}
        </div>
        <ListDashboardResults
          contentType="content-list"
          location={location}
          onDelete={noop}
          onAddList={noop}
          hideFilters
        />
        {
          !isLoading && !isSearchingOrSorting && (
            <Pagination
              location={location}
              meta={pagination}
              links={pagination.links}
              onClick={this.handlePageChange}
            />
          )
        }
      </div>
    );
  }
}

const mapStateToProps = (state): StateProps => {
  const { organizations, lists } = state;

  return ({
    activeBrandFilter: getActiveBrandFilterSelector(state),
    currentOrganization: organizations.currentOrganization,
    isLoading: lists.isLoading,
    isSearchingOrSorting: lists.isSearchingOrSorting,
    screenState: lists.screenState
  });
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchDashboardLists: (
    orgId: number,
    type: string,
    filter: string,
    page: number,
    sort: string,
    search: string,
    perPage: number,
    isSearchingOrSorting: boolean
  ): Promise<void> => dispatch(
    fetchDashboardContentLists(orgId, type, filter, page, sort, search, perPage, isSearchingOrSorting)
  ),

  dispatchResetLists: (): void => dispatch(resetLists())
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentListsDashboard);
